import { isJsonObject } from "shared/helpers/isJsonObject";

export enum Platform {
  Android = "android",
  iOS = "ios",
  Web = "web",
}

export enum Orientation {
  Portrait = "portrait",
  Landscape = "landscape",
}

/**
 * The device booleans on their own, without the deprecated `platform` value and
 * without the screen measurements. `DeviceInfo` extends this rather than the
 * other way around so that everything reading the flags - the admin
 * `platformConfig` checkboxes especially - stays limited to booleans.
 */
export interface DeviceInfoPlatformFlags {
  isNativeApp: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isWeb: boolean;
}

export interface DeviceInfo extends DeviceInfoPlatformFlags {
  /**
   * @deprecated Use `getPlatform` instead.
   */
  platform: Platform;
  /**
   * Which way the screen is turned right now. A square screen reads as
   * portrait.
   */
  orientation?: Orientation;
  /**
   * The screen width in CSS pixels - the same number media queries use, not the
   * device's physical pixel count.
   */
  width?: number;
  /**
   * The screen height in CSS pixels - the same number media queries use, not the
   * device's physical pixel count.
   */
  height?: number;
}

/**
 * The same flags where a missing flag is meaningful - configuration reads an
 * absent flag as "not decided" rather than as `false`.
 */
export type PartialDeviceInfoPlatformFlags = Partial<DeviceInfoPlatformFlags>;

/**
 * The screen a button is allowed to show on, on top of the platform flags.
 * Every field is optional and an absent field means the constraint does not
 * apply - a blank width reads as "any width", never as "zero width".
 */
export type DeviceInfoScreenConstraints = {
  /**
   * Only show at this orientation. Absent means either orientation.
   */
  orientation?: Orientation;
  /**
   * Only show when the screen is this wide or narrower, in CSS pixels - the
   * same number media queries use, not the device's physical pixel count.
   */
  maxWidth?: number;
  /**
   * Only show when the screen is this tall or shorter, in CSS pixels.
   */
  maxHeight?: number;
};

/**
 * Everything one button's visibility can be configured on - which platforms it
 * shows on and which screens it shows on.
 */
export type DeviceInfoVisibilityConfig = PartialDeviceInfoPlatformFlags &
  DeviceInfoScreenConstraints;

export const deviceInfoPlatformFlagKeys = [
  "isNativeApp",
  "isIOS",
  "isAndroid",
  "isWeb",
] as const satisfies readonly (keyof DeviceInfoPlatformFlags)[];

export const isPartialDeviceInfoPlatformFlags = (
  value: unknown,
): value is PartialDeviceInfoPlatformFlags =>
  isJsonObject(value) &&
  deviceInfoPlatformFlagKeys.every(
    (key) => value[key] === undefined || typeof value[key] === "boolean",
  );

export const deviceInfoScreenBoundKeys = [
  "maxWidth",
  "maxHeight",
] as const satisfies readonly (keyof DeviceInfoScreenConstraints)[];

export const isOrientation = (value: unknown): value is Orientation =>
  typeof value === "string" &&
  (Object.values(Orientation) as string[]).includes(value);

/**
 * A screen bound is a whole number of pixels above zero. Rejecting 0, negatives
 * and fractions means a nonsense stored value reads as "not configured" rather
 * than as a rule no screen can satisfy.
 */
const isScreenBound = (value: unknown): boolean =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

export const isDeviceInfoVisibilityConfig = (
  value: unknown,
): value is DeviceInfoVisibilityConfig => {
  if (!isJsonObject(value)) {
    return false;
  }
  const { orientation } = value;
  return (
    (orientation === undefined || isOrientation(orientation)) &&
    deviceInfoScreenBoundKeys.every(
      (key) => value[key] === undefined || isScreenBound(value[key]),
    ) &&
    isPartialDeviceInfoPlatformFlags(value)
  );
};

/**
 * Resolves the `Platform` a set of device flags represents. Web is the fallback
 * because it is the platform the app runs on when nothing native is detected.
 */
export const getPlatform = ({
  isIOS,
  isAndroid,
}: Pick<
  DeviceInfoPlatformFlags,
  "isWeb" | "isIOS" | "isAndroid"
>): Platform => {
  if (isIOS) {
    return Platform.iOS;
  }
  if (isAndroid) {
    return Platform.Android;
  }
  return Platform.Web;
};

/**
 * Resolves the `Orientation` a screen's dimensions represent. Portrait is the
 * fallback because a square screen is not landscape.
 */
export const getOrientation = ({
  width,
  height,
}: {
  width: number;
  height: number;
}): Orientation =>
  width > height ? Orientation.Landscape : Orientation.Portrait;
